import { type EvidenceLevel, type ProgramRuleView, RULE_FIELD_SPECS, type RuleField, type RuleOperator } from '@tex/finance-shared'
import { humanize } from './format'

/**
 * Draft shape of one rule while it is being edited. Values are kept as strings so half-typed
 * numbers survive re-renders; `toRules` converts and validates before the API call (the API
 * validates again with zod against the same allowlist).
 */
export interface RuleDraft {
  field: RuleField
  operator: RuleOperator
  single: string
  list: string[]
  low: string
  high: string
  min_evidence: EvidenceLevel | ''
  deferred_to_partner: boolean
}

export function ruleDraftFrom(rule: ProgramRuleView): RuleDraft {
  const base = {
    field: rule.field,
    operator: rule.operator,
    single: '',
    list: [] as string[],
    low: '',
    high: '',
    min_evidence: rule.min_evidence ?? ('' as const),
    deferred_to_partner: rule.deferred_to_partner ?? false,
  }
  switch (rule.operator) {
    case 'eq':
      return { ...base, single: rule.value }
    case 'in':
    case 'not_in':
      return { ...base, list: rule.value }
    case 'gte':
    case 'lte':
      return { ...base, low: String(rule.value) }
    case 'between':
      return {
        ...base,
        low: String(rule.value[0]),
        high: String(rule.value[1]),
      }
  }
}

export function emptyRuleDraft(field: RuleField = 'borrower.state'): RuleDraft {
  return {
    field,
    operator: RULE_FIELD_SPECS[field].operators[0],
    single: '',
    list: [],
    low: '',
    high: '',
    min_evidence: '',
    deferred_to_partner: false,
  }
}

export class RuleDraftError extends Error {
  constructor(
    public readonly index: number,
    message: string,
  ) {
    super(message)
    this.name = 'RuleDraftError'
  }
}

function parseInteger(raw: string, index: number, label: string): number {
  const n = Number(raw)
  if (raw.trim() === '' || !Number.isInteger(n)) throw new RuleDraftError(index, `${label}: enter a whole number`)
  return n
}

export function toRules(drafts: RuleDraft[]): ProgramRuleView[] {
  return drafts.map((d, i) => {
    const spec = RULE_FIELD_SPECS[d.field]
    const extras = {
      ...(d.min_evidence ? { min_evidence: d.min_evidence } : {}),
      ...(d.deferred_to_partner ? { deferred_to_partner: true } : {}),
    }
    if (!(spec.operators as readonly RuleOperator[]).includes(d.operator)) {
      throw new RuleDraftError(i, `${spec.label} does not support ${d.operator}`)
    }
    switch (d.operator) {
      case 'eq':
        if (!d.single.trim()) throw new RuleDraftError(i, `${spec.label}: choose a value`)
        return {
          field: d.field,
          operator: 'eq',
          value: d.single.trim(),
          ...extras,
        }
      case 'in':
      case 'not_in': {
        const list = d.list.map((v) => v.trim()).filter(Boolean)
        if (list.length === 0) throw new RuleDraftError(i, `${spec.label}: choose at least one value`)
        return { field: d.field, operator: d.operator, value: list, ...extras }
      }
      case 'gte':
      case 'lte':
        return {
          field: d.field,
          operator: d.operator,
          value: parseInteger(d.low, i, spec.label),
          ...extras,
        }
      case 'between': {
        const low = parseInteger(d.low, i, `${spec.label} (from)`)
        const high = parseInteger(d.high, i, `${spec.label} (to)`)
        if (high < low) throw new RuleDraftError(i, `${spec.label}: "to" must not be below "from"`)
        return {
          field: d.field,
          operator: 'between',
          value: [low, high],
          ...extras,
        }
      }
    }
  })
}

export const OPERATOR_LABELS: Record<RuleOperator, string> = {
  eq: 'is',
  in: 'is one of',
  not_in: 'is not one of',
  gte: 'is at least',
  lte: 'is at most',
  between: 'is between',
}

/** Compact one-line rendering of a stored rule, for read-only views and evaluation snapshots. */
export function describeRule(rule: ProgramRuleView): string {
  const label = RULE_FIELD_SPECS[rule.field]?.label ?? rule.field
  const value = Array.isArray(rule.value) ? rule.value.join(', ') : String(rule.value)
  const suffix = [rule.min_evidence ? `min evidence ${humanize(rule.min_evidence)}` : null, rule.deferred_to_partner ? 'deferred to partner' : null]
    .filter(Boolean)
    .join('; ')
  return `${label} ${OPERATOR_LABELS[rule.operator]} ${value}${suffix ? ` (${suffix})` : ''}`
}
